import { cn } from "@/lib/utils";

export function PageHeader({ title, subtitle, icon: Icon, actions, badge }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 animate-fadeUp">
      <div className="flex items-start gap-4">
        {Icon && (
          <div className="hidden sm:flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl panel">
            <Icon size={24} weight="duotone" className="text-[#00E5FF]" />
          </div>
        )}
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white">{title}</h1>
            {badge}
          </div>
          {subtitle && <p className="text-zinc-400 mt-1.5 text-sm sm:text-base max-w-2xl">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

export function Panel({ className, children, hover, ...props }) {
  return (
    <div className={cn("panel p-6", hover && "panel-hover", className)} {...props}>
      {children}
    </div>
  );
}

export function SectionTitle({ children, right }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-display text-lg font-bold text-white">{children}</h3>
      {right}
    </div>
  );
}

const STATUS_MAP = {
  LIVE: "text-[#00FF9D] bg-[#00FF9D]/10 border-[#00FF9D]/30",
  TEST: "text-[#FFB800] bg-[#FFB800]/10 border-[#FFB800]/30",
  MOCK: "text-[#8B5CF6] bg-[#8B5CF6]/10 border-[#8B5CF6]/30",
  "NOT CONNECTED": "text-zinc-400 bg-zinc-500/10 border-zinc-500/30",
  ERROR: "text-[#FF3366] bg-[#FF3366]/10 border-[#FF3366]/30",
  APPROVED: "text-[#00FF9D] bg-[#00FF9D]/10 border-[#00FF9D]/30",
  PUBLISHED: "text-[#00E5FF] bg-[#00E5FF]/10 border-[#00E5FF]/30",
  "READY FOR REVIEW": "text-[#FFB800] bg-[#FFB800]/10 border-[#FFB800]/30",
  GENERATING: "text-[#00E5FF] bg-[#00E5FF]/10 border-[#00E5FF]/30 animate-pulseGlow",
  GENERATED: "text-[#8B5CF6] bg-[#8B5CF6]/10 border-[#8B5CF6]/30",
  SUGGESTED: "text-zinc-300 bg-zinc-500/10 border-zinc-500/30",
  "NOT CREATED": "text-zinc-500 bg-zinc-500/5 border-zinc-600/20",
  PASS: "text-[#00FF9D] bg-[#00FF9D]/10 border-[#00FF9D]/30",
  REVIEW: "text-[#FFB800] bg-[#FFB800]/10 border-[#FFB800]/30",
  BLOCKED: "text-[#FF3366] bg-[#FF3366]/10 border-[#FF3366]/30",
};

export function StatusBadge({ status, className }) {
  const cls = STATUS_MAP[status] || "text-zinc-300 bg-zinc-500/10 border-zinc-500/30";
  return (
    <span data-testid={`status-${String(status).toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className={cn("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide font-mono", cls, className)}>
      {status}
    </span>
  );
}

export function Tag({ children, className }) {
  return (
    <span className={cn("inline-flex items-center rounded-full border border-white/10 bg-white/5 px-2.5 py-0.5 text-xs text-zinc-300", className)}>
      {children}
    </span>
  );
}

export function StatCard({ label, value, sub, icon: Icon, accent = "#00E5FF", testid }) {
  return (
    <div data-testid={testid} className="panel panel-hover p-5 relative overflow-hidden">
      <div className="absolute -right-6 -top-6 h-20 w-20 rounded-full opacity-20 blur-2xl" style={{ background: accent }} />
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-zinc-500 font-medium">{label}</span>
        {Icon && <Icon size={18} weight="duotone" style={{ color: accent }} />}
      </div>
      <div className="mt-3 font-display text-2xl sm:text-3xl font-extrabold text-white">{value}</div>
      {sub && <div className="mt-1 text-xs text-zinc-500">{sub}</div>}
    </div>
  );
}

export function MockBanner({ children }) {
  return (
    <div className="mb-6 flex items-start gap-3 rounded-xl border border-[#8B5CF6]/30 bg-[#8B5CF6]/8 px-4 py-3">
      <span className="mt-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-[#8B5CF6] border border-[#8B5CF6]/40 rounded px-1.5 py-0.5">Mock</span>
      <p className="text-sm text-zinc-300">{children}</p>
    </div>
  );
}

export function Loading({ label = "Loading studio…" }) {
  return (
    <div className="flex h-64 flex-col items-center justify-center gap-3 text-zinc-500">
      <div className="h-8 w-8 rounded-full border-2 border-white/10 border-t-[#00E5FF] animate-spin" />
      <span className="text-sm">{label}</span>
    </div>
  );
}
